import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { personDisciplinaerStatusForm } from "../../../../../config/form/person/personDisciplinaerStatusFormConfig";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import {
  PersonDisciplinaerstatus,
  PersonDto,
} from "../../../../../interfaces/schema";
import { PersonDisciplinaerStatusField } from "../../../../../interfaces/personer";
import { SortBy, IColumn } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

export default function DisciplinaerStatus({
  columns,
  disciplinaerStatus,
  setActiveColumns,
  defaultColumns,
  personId,
  invalidateData,
}: {
  personId: string;
  disciplinaerStatus: PersonDisciplinaerstatus[];
  defaultColumns: IColumn<PersonDisciplinaerStatusField>[];
  columns: IColumn<PersonDisciplinaerStatusField>[];
  setActiveColumns: React.Dispatch<
    React.SetStateAction<PersonDisciplinaerStatusField[]>
  >;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "DisciplinaertWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personDisciplinaerStatusSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const { data } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id: personId },
  });
  // event handlers
  function downloadHandler() {
    tableToXLSX(columns, disciplinaerStatus, {
      fileName: `disciplinærstatus-for-${data?.person.kundeNr}`,
      additionalData: {
        kundenr: data?.person.kundeNr,
      },
    });
  }

  if (disciplinaerStatus.length === 0) {
    return (
      <IngenRegistreringer
        editConfig={
          isAuth ? personDisciplinaerStatusForm("", personId) : undefined
        }
        invalidateData={invalidateData}
      />
    );
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <ModalProvider>
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <th className="indicator"></th>
            <>
              {getHeaderActiveColumns(columns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <TableBody>
            {orderBy(
              disciplinaerStatus,
              sortBy.map((s) => s.field),
              sortBy.map((s) => s.direction),
            ).map((ds, i) => {
              return (
                <TableRow
                  key={i}
                  editConfig={
                    isAuth
                      ? personDisciplinaerStatusForm(ds.id, personId)
                      : undefined
                  }
                  invalidateData={invalidateData}
                >
                  <td
                    className={`indicator${
                      periodeIsActive(
                        new Date(ds.periode.fraDato),
                        new Date(ds.periode.tilDatoInklusiv),
                      )
                        ? " has-effect"
                        : ""
                    }`}
                  >
                    <ActiveIcon />
                  </td>
                  <>
                    {columns
                      .filter((col) => col.active)
                      .map((col) => {
                        return <TD key={col.field} col={col} rowData={ds} />;
                      })}
                  </>
                </TableRow>
              );
            })}
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          columns={columns}
          setActiveColumns={setActiveColumns}
          defaultColumns={defaultColumns}
        />
        <OpretNyButtonBlock
          editConfig={
            isAuth ? personDisciplinaerStatusForm("", personId) : undefined
          }
          invalidateData={invalidateData}
        />
      </ModalProvider>
    </>
  );
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];
